import { useEffect, useRef, useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FixedCostCurrency, LocalAiLeadConfig } from "@/api/admin-api"

import {
  appendLocalAiLeadAnnualCost,
  appendLocalAiLeadFx,
  enableLocalAiLeadAnnualCost,
  getLocalAiLeadCostLedger,
} from "@/api/admin-api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuthGeneration } from "@/hooks/use-auth-generation"

export function LocalAiLeadCostModelSection({ config }: { config: LocalAiLeadConfig }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [yearMonth, setYearMonth] = useState(() => new Date().toISOString().slice(0, 7))
  const authGeneration = useAuthGeneration()
  const query = useQuery({
    queryKey: ["admin", "local-ai-lead-cost-ledger", yearMonth, authGeneration],
    queryFn: () => getLocalAiLeadCostLedger(yearMonth),
    refetchOnWindowFocus: false,
  })
  const versions = query.data?.costVersions ?? []
  const fxVersions = query.data?.fxVersions ?? []
  const latest = versions.at(-1)
  const latestFx = fxVersions.at(-1)
  const enabled = config.cost_model === "shared_annual_v1"
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10))
  const [currency, setCurrency] = useState<FixedCostCurrency>(config.reporting_currency ?? "USD")
  const [subscription, setSubscription] = useState("")
  const [infrastructure, setInfrastructure] = useState("")
  const [projectWork, setProjectWork] = useState("")
  const [benchmark, setBenchmark] = useState("")
  const [fxRate, setFxRate] = useState("")
  const poolInputs = [subscription, infrastructure, projectWork]
  const annualPool = poolInputs.every(
    (value) => value.trim() !== "" && Number.isFinite(Number(value)) && Number(value) >= 0
  )
    ? poolInputs.reduce((sum, value) => sum + Number(value), 0)
    : null
  const poolMoney = (amount: number) =>
    new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  const loadedCostId = useRef<string | null>(null)
  const loadedFxKey = useRef<string | null>(null)

  useEffect(() => {
    if (!latest || latest.id === loadedCostId.current) return
    loadedCostId.current = latest.id
    setEffectiveFrom(latest.effectiveFrom)
    setCurrency(latest.currency)
    setSubscription(String(latest.annualSubscription))
    setInfrastructure(String(latest.annualCloudInfrastructure))
    setProjectWork(String(latest.annualProjectWork))
    setBenchmark(String(latest.annualBenchmark))
  }, [latest])
  useEffect(() => {
    const key = `${yearMonth}:${latestFx?.id ?? "none"}`
    if (key === loadedFxKey.current) return
    loadedFxKey.current = key
    setFxRate(latestFx ? String(latestFx.USDToReportingCurrency) : "")
  }, [latestFx, yearMonth])

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "local-ai-lead-cost-ledger"] })
    await queryClient.invalidateQueries({ queryKey: ["admin", "local-ai-lead-config"] })
    await queryClient.invalidateQueries({ queryKey: ["localAiLead", "costBreakdown"] })
    await queryClient.invalidateQueries({ queryKey: ["admin", "local-ai-lead-group-overview"] })
  }
  const costMutation = useMutation({
    mutationFn: appendLocalAiLeadAnnualCost,
    onSuccess: invalidate,
  })
  const fxMutation = useMutation({ mutationFn: appendLocalAiLeadFx, onSuccess: invalidate })
  const enableMutation = useMutation({
    mutationFn: enableLocalAiLeadAnnualCost,
    onSuccess: invalidate,
  })

  const saveCosts = async () => {
    const amounts = [subscription, infrastructure, projectWork, benchmark].map(Number)
    if (
      [subscription, infrastructure, projectWork, benchmark].some((value) => value.trim() === "") ||
      amounts.some((value) => !Number.isFinite(value) || value < 0) ||
      !effectiveFrom
    ) {
      toast.error(
        t(
          "localAiLead.costModel.invalid",
          "Enter an effective date and nonnegative annual amounts."
        )
      )
      return
    }
    try {
      await costMutation.mutateAsync({
        expectedRevision: latest?.revision ?? 0,
        effectiveFrom,
        currency,
        annualSubscription: amounts[0],
        annualCloudInfrastructure: amounts[1],
        annualProjectWork: amounts[2],
        annualBenchmark: amounts[3],
      })
      toast.success(t("localAiLead.costModel.saved", "Annual cost version saved"))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }
  const saveFx = async () => {
    const rate = Number(fxRate)
    if (!Number.isFinite(rate) || rate <= 0) {
      toast.error(t("localAiLead.costModel.invalidFx", "Enter a positive USD conversion rate."))
      return
    }
    try {
      await fxMutation.mutateAsync({
        expectedRevision: latestFx?.revision ?? 0,
        yearMonth,
        USDToReportingCurrency: rate,
      })
      toast.success(t("localAiLead.costModel.fxSaved", "Monthly rate saved"))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }
  const enableModel = async () => {
    try {
      await enableMutation.mutateAsync()
      toast.success(t("localAiLead.costModel.enabled", "Shared annual cost model enabled"))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  return (
    <section
      aria-label={t("localAiLead.costModel.title", "Shared annual cost model")}
      className="space-y-4 rounded-xl border p-4"
    >
      <div>
        <h3 className="font-semibold">
          {t("localAiLead.costModel.title", "Shared annual cost model")}
        </h3>
        <p className="text-muted-foreground text-sm">
          {enabled
            ? t(
                "localAiLead.costModel.active",
                "Active. New revisions recompute historical estimates from their effective date."
              )
            : t(
                "localAiLead.costModel.inactive",
                "Legacy estimates remain active until you save annual costs and enable this model."
              )}
        </p>
      </div>
      {query.isError ? (
        <p className="text-destructive text-sm" role="alert">
          {t(
            "localAiLead.costModel.loadError",
            "Could not load the cost ledger. Check provisioning before enabling."
          )}
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="lail-effective-date">
            {t("localAiLead.costModel.effective", "Effective from (UTC)")}
          </Label>
          <Input
            id="lail-effective-date"
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="lail-reporting-currency">
            {t("localAiLead.costModel.currency", "Reporting currency")}
          </Label>
          <Select
            disabled={enabled || versions.length > 0}
            value={currency}
            onValueChange={(value) => setCurrency(value as FixedCostCurrency)}
          >
            <SelectTrigger id="lail-reporting-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="SEK">SEK</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(
          [
            ["subscription", "Platform subscription / year", subscription, setSubscription],
            ["infrastructure", "Cloud infrastructure / year", infrastructure, setInfrastructure],
            ["project", "Projected project work / year", projectWork, setProjectWork],
            ["benchmark", "Seat benchmark / year", benchmark, setBenchmark],
          ] as const
        ).map(([key, label, value, setter]) => (
          <div key={key} className="space-y-1">
            <Label htmlFor={`lail-annual-${key}`}>{label}</Label>
            <Input
              id={`lail-annual-${key}`}
              min={0}
              step="0.01"
              type="number"
              value={value}
              onChange={(e) => setter(e.target.value)}
            />
          </div>
        ))}
      </div>
      {annualPool != null ? (
        <div className="bg-muted/40 space-y-2 rounded-lg border p-3 text-sm">
          <p className="font-medium">
            {t("localAiLead.costModel.pool", "Shared pool")}: {poolMoney(annualPool)} /{" "}
            {t("localAiLead.costModel.year", "year")} · {poolMoney(annualPool / 12)} /{" "}
            {t("localAiLead.costModel.month", "month")}
          </p>
          <div
            className="flex h-3 overflow-hidden rounded-full border"
            role="img"
            aria-label={t(
              "localAiLead.costModel.poolSplit",
              "Annual pool split: subscription, cloud infrastructure, project work"
            )}
          >
            <span
              className="bg-primary h-full"
              style={{ width: `${annualPool ? (Number(subscription) / annualPool) * 100 : 0}%` }}
            />
            <span
              className="bg-secondary h-full"
              style={{ width: `${annualPool ? (Number(infrastructure) / annualPool) * 100 : 0}%` }}
            />
            <span
              className="bg-accent h-full"
              style={{ width: `${annualPool ? (Number(projectWork) / annualPool) * 100 : 0}%` }}
            />
          </div>
          <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <span>
              {t("localAiLead.costModel.subscription", "Subscription")}:{" "}
              {poolMoney(Number(subscription))}
            </span>
            <span>
              {t("localAiLead.costModel.infrastructure", "Cloud infrastructure")}:{" "}
              {poolMoney(Number(infrastructure))}
            </span>
            <span>
              {t("localAiLead.costModel.projectWork", "Project work")}:{" "}
              {poolMoney(Number(projectWork))}
            </span>
          </div>
        </div>
      ) : null}
      <Button
        disabled={query.isLoading || query.isError || costMutation.isPending}
        onClick={() => void saveCosts()}
      >
        {t("localAiLead.costModel.saveVersion", "Save annual cost version")}
      </Button>
      <div className="grid gap-3 border-t pt-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="lail-fx-month">{t("localAiLead.costModel.fxMonth", "Rate month")}</Label>
          <Input
            id="lail-fx-month"
            type="month"
            value={yearMonth}
            onChange={(e) => setYearMonth(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="lail-fx-rate">
            {t("localAiLead.costModel.fxRate", "USD to reporting currency")}
          </Label>
          <Input
            disabled={currency === "USD"}
            id="lail-fx-rate"
            min="0.000001"
            step="any"
            type="number"
            value={fxRate}
            onChange={(e) => setFxRate(e.target.value)}
          />
        </div>
        {currency !== "USD" ? (
          <Button
            disabled={fxMutation.isPending || query.isError}
            variant="secondary"
            onClick={() => void saveFx()}
          >
            {t("localAiLead.costModel.saveFx", "Save monthly rate")}
          </Button>
        ) : null}
      </div>
      {!enabled ? (
        <Button
          disabled={!latest || enableMutation.isPending || query.isError}
          variant="secondary"
          onClick={() => void enableModel()}
        >
          {t("localAiLead.costModel.enable", "Enable shared annual model")}
        </Button>
      ) : null}
    </section>
  )
}
